import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/components/feature/Footer';

type TabType = 'privacy' | 'terms';

export default function LegalPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<TabType>('privacy');

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'terms' || tab === 'privacy') {
      setActiveTab(tab);
    }
  }, [searchParams]);

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    setSearchParams({ tab });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Navbar />

      {/* Banner */}
      <section className="relative pt-24 pb-12 bg-[#faf7f4]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="flex items-center justify-center gap-2 text-xs text-[#C8714A]/70 mb-4">
            <Link to="/" className="hover:text-[#C8714A] transition-colors">{t('nav_home')}</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-[#2a1a0e]/50">{t('legal_breadcrumb')}</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-[#2a1a0e] mb-3">
            {t('legal_banner_title')}
          </h1>
          <p className="text-[#2a1a0e]/50 text-sm md:text-base">
            {t('legal_banner_desc')}
          </p>
        </div>
      </section>

      {/* Tab Switcher */}
      <div className="sticky top-0 z-10 bg-white border-b border-[#f0ebe5]">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex gap-0">
            <button
              onClick={() => handleTabChange('privacy')}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors whitespace-nowrap cursor-pointer ${
                activeTab === 'privacy'
                  ? 'border-[#C8714A] text-[#C8714A]'
                  : 'border-transparent text-[#2a1a0e]/50 hover:text-[#2a1a0e]'
              }`}
            >
              {t('footer_privacy')}
            </button>
            <button
              onClick={() => handleTabChange('terms')}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors whitespace-nowrap cursor-pointer ${
                activeTab === 'terms'
                  ? 'border-[#C8714A] text-[#C8714A]'
                  : 'border-transparent text-[#2a1a0e]/50 hover:text-[#2a1a0e]'
              }`}
            >
              {t('footer_terms')}
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="flex-1 py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-6">
          {activeTab === 'privacy' ? <PrivacyContent /> : <TermsContent />}
        </div>
      </main>

      <Footer />
    </div>
  );
}

function PrivacyContent() {
  const { t } = useTranslation();
  return (
    <article className="prose-legal">
      <div className="flex items-center gap-3 mb-8">
        <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#C8714A]/10">
          <i className="ri-shield-check-line text-[#C8714A] text-lg"></i>
        </div>
        <div>
          <h2 className="text-xl font-bold text-[#2a1a0e]">{t('footer_privacy')}</h2>
          <p className="text-xs text-[#2a1a0e]/40">{t('legal_updated')}: 2026-01-01</p>
        </div>
      </div>

      <LegalSection icon="ri-information-line" title={t('privacy_s1_title')}>
        <p>{t('privacy_s1_p1')}</p>
        <p>{t('privacy_s1_p2')}</p>
      </LegalSection>

      <LegalSection icon="ri-database-2-line" title={t('privacy_s2_title')}>
        <p>{t('privacy_s2_p1')}</p>
        <ul>
          <li>{t('privacy_s2_li1')}</li>
          <li>{t('privacy_s2_li2')}</li>
          <li>{t('privacy_s2_li3')}</li>
          <li>{t('privacy_s2_li4')}</li>
        </ul>
      </LegalSection>

      <LegalSection icon="ri-settings-3-line" title={t('privacy_s3_title')}>
        <p>{t('privacy_s3_p1')}</p>
        <ul>
          <li>{t('privacy_s3_li1')}</li>
          <li>{t('privacy_s3_li2')}</li>
          <li>{t('privacy_s3_li3')}</li>
        </ul>
      </LegalSection>

      <LegalSection icon="ri-share-line" title={t('privacy_s4_title')}>
        <p>{t('privacy_s4_p1')}</p>
        <p>{t('privacy_s4_p2')}</p>
      </LegalSection>

      <LegalSection icon="ri-lock-line" title={t('privacy_s5_title')}>
        <p>{t('privacy_s5_p1')}</p>
      </LegalSection>

      <LegalSection icon="ri-user-settings-line" title={t('privacy_s6_title')}>
        <p>{t('privacy_s6_p1')}</p>
        <ul>
          <li>{t('privacy_s6_li1')}</li>
          <li>{t('privacy_s6_li2')}</li>
          <li>{t('privacy_s6_li3')}</li>
        </ul>
      </LegalSection>

      <LegalSection icon="ri-mail-line" title={t('privacy_s7_title')}>
        <p>{t('privacy_s7_p1')}</p>
        <p className="font-medium text-[#C8714A]">cs@koorpet.com</p>
      </LegalSection>
    </article>
  );
}

function TermsContent() {
  const { t } = useTranslation();
  return (
    <article className="prose-legal">
      <div className="flex items-center gap-3 mb-8">
        <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#C8714A]/10">
          <i className="ri-file-text-line text-[#C8714A] text-lg"></i>
        </div>
        <div>
          <h2 className="text-xl font-bold text-[#2a1a0e]">{t('footer_terms')}</h2>
          <p className="text-xs text-[#2a1a0e]/40">{t('legal_updated')}: 2026-01-01</p>
        </div>
      </div>

      <LegalSection icon="ri-checkbox-circle-line" title={t('terms_s1_title')}>
        <p>{t('terms_s1_p1')}</p>
        <p>{t('terms_s1_p2')}</p>
      </LegalSection>

      <LegalSection icon="ri-shopping-bag-line" title={t('terms_s2_title')}>
        <p>{t('terms_s2_p1')}</p>
        <ul>
          <li>{t('terms_s2_li1')}</li>
          <li>{t('terms_s2_li2')}</li>
          <li>{t('terms_s2_li3')}</li>
        </ul>
      </LegalSection>

      <LegalSection icon="ri-truck-line" title={t('terms_s3_title')}>
        <p>{t('terms_s3_p1')}</p>
        <p>{t('terms_s3_p2')}</p>
      </LegalSection>

      <LegalSection icon="ri-refund-2-line" title={t('terms_s4_title')}>
        <p>{t('terms_s4_p1')}</p>
        <ul>
          <li>{t('terms_s4_li1')}</li>
          <li>{t('terms_s4_li2')}</li>
          <li>{t('terms_s4_li3')}</li>
        </ul>
      </LegalSection>

      <LegalSection icon="ri-copyright-line" title={t('terms_s5_title')}>
        <p>{t('terms_s5_p1')}</p>
        <p>{t('terms_s5_p2')}</p>
      </LegalSection>

      <LegalSection icon="ri-error-warning-line" title={t('terms_s6_title')}>
        <p>{t('terms_s6_p1')}</p>
      </LegalSection>

      <LegalSection icon="ri-scales-line" title={t('terms_s7_title')}>
        <p>{t('terms_s7_p1')}</p>
      </LegalSection>

      <LegalSection icon="ri-mail-line" title={t('terms_s8_title')}>
        <p>{t('terms_s8_p1')}</p>
        <p className="font-medium text-[#C8714A]">cs@koorpet.com</p>
      </LegalSection>
    </article>
  );
}

interface LegalSectionProps {
  icon: string;
  title: string;
  children: React.ReactNode;
}

function LegalSection({ icon, title, children }: LegalSectionProps) {
  return (
    <section className="mb-10 pb-10 border-b border-[#f0ebe5] last:border-0 last:mb-0 last:pb-0">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-6 h-6 flex items-center justify-center">
          <i className={`${icon} text-[#C8714A] text-base`}></i>
        </div>
        <h3 className="text-base font-semibold text-[#2a1a0e]">{title}</h3>
      </div>
      <div className="pl-8 space-y-3 text-sm text-[#2a1a0e]/65 leading-relaxed [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1.5 [&_p]:text-[#2a1a0e]/65">
        {children}
      </div>
    </section>
  );
}
